/**
 * ============================================================================
 *                              crmss 版权声明
 * ============================================================================
 * 本软件系统（crmss - 快速开发通用管理后台框架）所有代码版权归 crmss 团队所有。
 * 代码中的 crmss 版权声明不允许删除和修改。
 * ============================================================================
 * 用户状态管理 - 支持热更新、自动加载
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import Cookies from 'js-cookie'
import { loginApi, logoutApi, getUserInfoApi, type LoginParams } from '@/api/login'

/**
 * crmss 用户状态管理标识
 */
export const CRMSS_USER_STORE_FLAG = 'CRMSS_USER_STORE'

// crmss Token Key
const CRMSS_TOKEN_KEY = 'crmss_token'
const CRMSS_USER_INFO_KEY = 'crmss_user_info'

export const useUserStore = defineStore('user', () => {
  // State
  const token = ref<string>(Cookies.get(CRMSS_TOKEN_KEY) || '')
  const userInfo = ref<any>({})
  const roles = ref<string[]>([])
  const permissions = ref<string[]>([])
  const isLoggedIn = computed(() => !!token.value)

  // Getters
  const username = computed(() => userInfo.value.nickname || userInfo.value.phone || '')
  const avatar = computed(() => userInfo.value.avatar || '')
  const userId = computed(() => userInfo.value.userId || '')

  /**
   * 设置 Token
   */
  const setToken = (newToken: string) => {
    token.value = newToken
    Cookies.set(CRMSS_TOKEN_KEY, newToken, { expires: 7 })
  }

  /**
   * 清除 Token
   */
  const clearToken = () => {
    token.value = ''
    Cookies.remove(CRMSS_TOKEN_KEY)
    Cookies.remove(CRMSS_USER_INFO_KEY)
  }

  /**
   * 登录 - 自动对接后端接口
   */
  const login = async (loginData: LoginParams) => {
    try {
      const res = await loginApi(loginData)
      if (res.code === 200 && res.data) {
        setToken(res.data.accessToken)
        userInfo.value = res.data.userInfo
        
        // 保存用户信息到 Cookie
        Cookies.set(CRMSS_USER_INFO_KEY, JSON.stringify(res.data.userInfo), { expires: 7 })
        
        return { success: true, data: res.data }
      }
      return { success: false, message: res.message }
    } catch (error: any) {
      return { success: false, message: error.message || '登录失败' }
    }
  }

  /**
   * 获取用户信息 - 自动对接后端接口
   */
  const getUserInfo = async () => {
    try {
      const res = await getUserInfoApi()
      if (res.code === 200 && res.data) {
        userInfo.value = res.data
        roles.value = ['user']
        permissions.value = ['*:*:*']
        return { success: true, data: res.data }
      }
      return { success: false, message: res.message }
    } catch (error: any) {
      return { success: false, message: error.message || '获取用户信息失败' }
    }
  }

  /**
   * 退出登录 - 自动对接后端接口
   */
  const logout = async () => {
    try {
      await logoutApi()
    } catch (error) {
      console.error('[crmss] 登出请求失败:', error)
    } finally {
      clearToken()
      userInfo.value = {}
      roles.value = []
      permissions.value = []
    }
  }

  /**
   * 初始化用户信息 - 从 Cookie 恢复
   */
  const initUserInfo = () => {
    const savedUserInfo = Cookies.get(CRMSS_USER_INFO_KEY)
    if (savedUserInfo) {
      try {
        userInfo.value = JSON.parse(savedUserInfo)
      } catch (e) {
        console.error('[crmss] 解析用户信息失败:', e)
      }
    }
  }

  return {
    token,
    userInfo,
    roles,
    permissions,
    isLoggedIn,
    username,
    avatar,
    userId,
    setToken,
    clearToken,
    login,
    getUserInfo,
    logout,
    initUserInfo
  }
})
